import { Box, Skeleton, Typography, pn } from '@compass/core';
import { Column, Summary, TotalItem } from 'devextreme-react/data-grid';
import { LkRoleId } from 'generated/openApi';
import { useState } from 'react';
import { useAppSelector } from 'hooks';
import { DevExpressReport } from 'components';
import { DashboardCard } from '../components/DashboardCard';
import { useGPASubmissionData } from '../contexts/GPASubmissionDataContext';
import { GPASubmissionRow } from './types';
import { GPARawDataDialog } from './GPARawDataDialog';
import { GPASubmissionFilterControls } from './GPASubmissionFilterControls';

export const GPASubmissionTableCard = () => {
    const { data, loading, error, filter } = useGPASubmissionData();
    const appRoleId = useAppSelector(state => state.app.roleId);
    const isCobroUser = appRoleId === LkRoleId.Cobro;
    const [rawDataOpen, setRawDataOpen] = useState(false);
    const [showFileId, setShowFileId] = useState(false);
    const totalStudents = data.reduce((total, row) => total + row.studentCount, 0);
    const totalEnrolled = data.reduce((total, row) => total + row.enrolledStudentTotal, 0);
    const totalPercentage = totalEnrolled > 0 ? totalStudents / totalEnrolled * 100 : 0;
    const handleExport = () => {
        const quote = (value: string | number | undefined) => `"${String(value ?? '').replace(/"/g, '""')}"`;
        const header = ['School Year', 'School', 'Grade Level', 'Term Type', 'Term Number', 'Students', 'Enrolled Student Total', '% Submitted'];
        if (isCobroUser && showFileId) header.push('File ID');
        const rows = data.map(row => [
            `${row.year}-${row.year + 1}`, row.schoolName, row.gradeLevelName, row.termTypeName, row.termNumber,
            row.studentCount, row.enrolledStudentTotal, `${row.percentageSubmitted}%`
        ].concat(isCobroUser && showFileId ? [row.fileIds] : []).map(quote).join(','));
        const blob = new Blob([[header.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a'); link.href = url; link.download = `GPA_Submission_${filter.schoolYears.join('_') || 'CurrentYear'}.csv`;
        document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
    };

    return <DashboardCard title="GPA" onExport={handleExport} onRawData={() => setRawDataOpen(true)}><Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', overflow: 'hidden', isolation: 'isolate' }}>
        <GPASubmissionFilterControls usePopup showFileId={showFileId}
            onShowFileIdChange={isCobroUser ? setShowFileId : undefined} />
        {loading ? <Skeleton variant="rounded" height={400} /> : error ? <Typography color="error" align="center">{error}</Typography> :
        <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: '0 1 auto', minHeight: 0, overflow: 'auto', '& .dx-datagrid-header-panel': { display: 'none' } }}><DevExpressReport dashboardStyle dataSource={data} fullPageSize showColumnChooser={false}>
                <Column dataField={pn<GPASubmissionRow>('year')} caption="School Year" alignment="left" cellRender={({ value }) => `${value}-${value + 1}`} />
                <Column dataField={pn<GPASubmissionRow>('schoolName')} caption="School" alignment="left" />
                <Column dataField={pn<GPASubmissionRow>('gradeLevelName')} caption="Grade Level" alignment="right" />
                <Column dataField={pn<GPASubmissionRow>('termTypeName')} caption="Term Type" alignment="right" />
                <Column dataField={pn<GPASubmissionRow>('termNumber')} caption="Term Number" alignment="right" />
                <Column dataField={pn<GPASubmissionRow>('studentCount')} caption="Students" alignment="right" />
                <Column dataField={pn<GPASubmissionRow>('enrolledStudentTotal')} caption="Enrolled Student Total" alignment="right" />
                <Column dataField={pn<GPASubmissionRow>('percentageSubmitted')} caption="% Submitted" alignment="right" cellRender={({ value }) => `${value}%`} />
                {isCobroUser && showFileId && <Column dataField={pn<GPASubmissionRow>('fileIds')} caption="File ID" alignment="right" />}
                <Summary>
                    <TotalItem column={pn<GPASubmissionRow>('year')} customizeText={() => 'Total'} />
                    <TotalItem column={pn<GPASubmissionRow>('studentCount')} summaryType="sum"
                        customizeText={() => totalStudents.toLocaleString()} />
                    <TotalItem column={pn<GPASubmissionRow>('enrolledStudentTotal')} summaryType="sum"
                        customizeText={() => totalEnrolled.toLocaleString()} />
                    <TotalItem column={pn<GPASubmissionRow>('percentageSubmitted')}
                        customizeText={() => `${totalPercentage.toFixed(1)}%`} />
                </Summary>
            </DevExpressReport></Box>
        </Box>}
    </Box>
        {rawDataOpen && <GPARawDataDialog filter={filter} onClose={() => setRawDataOpen(false)} />}
    </DashboardCard>;
};
